import { _ } from '@sveltia/i18n';
import { isObject } from '@sveltia/utils/object';
import { LocalStorage } from '@sveltia/utils/storage';

import { goto, parseLocation } from '$lib/services/app/navigation';
import { backend, backendName, selectBackend } from '$lib/services/backends';
import { lockedBranch, mergeLockedBranch } from '$lib/services/backends/branch-access';
import { NOT_COLLABORATOR_ERROR_MESSAGE } from '$lib/services/backends/git/shared/errors';
import {
  createStaleCheck,
  dataGeneration,
  repositoryHead,
} from '$lib/services/backends/git/shared/head';
import { startRemoteChangePolling, stopRemoteChangePolling } from '$lib/services/backends/poll';
import { cmsConfig } from '$lib/services/config';
import { dataLoaded } from '$lib/services/contents';
import { arrayFileItems } from '$lib/services/contents/file/process';
import { resetDeployments } from '$lib/services/deployments';
import { resetPageLiveness } from '$lib/services/deployments/ping';
import { initDeployments } from '$lib/services/deployments/resolve';
import { user } from '$lib/services/user/account.svelte';
import { LEGACY_USER_STORAGE_KEYS, USER_STORAGE_KEY } from '$lib/services/user/constants';
import { decodeMagicLinkData } from '$lib/services/user/magic-link';
import { prefs } from '$lib/services/user/prefs.svelte';
import {
  publishingBranches,
  unpublishedEntries,
  unpublishedEntriesLoaded,
} from '$lib/services/workflow';
import { resetDeployingEntries } from '$lib/services/workflow/deploy';
import { loadUnpublishedEntries, startLoadingPullRequests } from '$lib/services/workflow/load';
import {
  forkedRepository,
  forkPermissionRequest,
  openAuthoringInitialized,
} from '$lib/services/workflow/open-authoring';

/**
 * @import { BackendService, InternalCmsConfig, User } from '$lib/types/private';
 */

/**
 * Context of the sign-in error.
 * @typedef {'authentication' | 'dataFetch'} SignInErrorContext
 */

const MAGIC_LINK_REGEX = /^\/signin\/(?<encodedData>.+)/;

export const auth = $state({
  /** @type {{ message: string, context: SignInErrorContext }} */
  signInError: { message: '', context: 'authentication' },
  /** Whether the user is not authenticated. */
  unauthenticated: true,
  /** Whether a sign-in is in progress. */
  signingIn: false,
  /**
   * Account that a magic link signs the user in to, while they’re asked whether to go ahead. The
   * link can be crafted by anyone, so it’s only used once the user has recognized the account.
   * @type {{ account: User, resolve: (confirmed: boolean) => void } | undefined}
   */
  magicLinkConfirmation: undefined,
});

/**
 * Remove the API keys and log-in credentials for integrations, such as translation services and
 * cloud storage, from the user preferences, so they don’t remain on the device once the user has
 * signed out. The deploy hook URL and its `Authorization` header go too, as either can carry a
 * secret that triggers a deployment. Other preferences, like the theme and language, are kept. This
 * is done on an explicit sign-out, but not when an expired or revoked token sends the user back to
 * the sign-in form, unless the credentials came with a magic link: see
 * {@link clearMagicLinkCredentials}.
 */
const clearIntegrationCredentials = () => {
  prefs.apiKeys = {};
  delete prefs.logins;
  delete prefs.deployHookURL;
  delete prefs.deployHookAuthHeader;
  delete prefs.magicLinkAccount;
};

/**
 * Remove the integration credentials copied with a magic link once the session it started has
 * ended, however it ended. Since anyone can craft a link, they may be someone else’s: left on the
 * device, they would carry over to the user’s own account, sending its uploads to someone else’s
 * cloud storage, its translations through someone else’s API keys and its deployments to someone
 * else’s hook. They’re kept as long as the link’s account is signed in, also on the next visit.
 * Checking the account, rather than only clearing them where a session ends, also covers another
 * tab, which doesn’t see the preferences this one has changed: signing in to another account there
 * leaves the credentials in storage until they’re loaded with that account’s session.
 * @param {User} [account] Account starting a session. Omit it when the session has ended.
 */
const clearMagicLinkCredentials = (account) => {
  const { magicLinkAccount } = prefs;

  if (
    magicLinkAccount &&
    (magicLinkAccount.backendName !== account?.backendName ||
      magicLinkAccount.id !== account?.id ||
      magicLinkAccount.login !== account?.login)
  ) {
    clearIntegrationCredentials();
  }
};

/**
 * Clear the cached user token so the sign-in form is shown on next load. The user caches left by
 * Netlify/Decap CMS, which may also hold a token, are removed as well, and so are the credentials
 * copied with a magic link.
 */
const clearUserCache = async () => {
  clearMagicLinkCredentials();
  await Promise.all([
    LocalStorage.set(USER_STORAGE_KEY, {}),
    ...LEGACY_USER_STORAGE_KEYS.map((key) => LocalStorage.delete(key)),
  ]);
  user.account = undefined;
  auth.unauthenticated = true;
};

/**
 * Clear the cached token for authentication/access errors so the sign-in form is shown instead of a
 * dead-end error. Don’t clear for configuration errors like missing branches or repositories where
 * the credentials are still valid.
 * @param {Error} error Exception to check if it’s an authentication error.
 */
const clearUserCacheIfNeeded = async (error) => {
  const isAuthError =
    typeof (/** @type {any} */ (error?.cause)?.status) === 'number' ||
    error?.message === NOT_COLLABORATOR_ERROR_MESSAGE;

  if (isAuthError) {
    await clearUserCache();
  }
};

/**
 * Reset the sign-in error.
 */
export const resetError = () => {
  auth.signInError = { message: '', context: 'authentication' };
};

/**
 * Log an authentication error on the UI and in the browser console.
 * @param {Error} ex Exception.
 * @param {SignInErrorContext} [context] Context of the error.
 */
export const logError = (ex, context = 'authentication') => {
  let message = /** @type {{ message: string }} */ (ex.cause)?.message || _('unexpected_error');

  if (ex.name === 'NotFoundError') {
    message = _('sign_in_error.not_project_root');
  }

  if (ex.name === 'AbortError') {
    message = _(
      backendName.current === 'local'
        ? 'sign_in_error.picker_dismissed'
        : 'sign_in_error.authentication_aborted',
    );
  }

  auth.signInError = { message, context };
  // eslint-disable-next-line no-console
  console.error(ex.name, ex.message, ex.cause);
};

/**
 * Parse a magic link URL generated for QR code sign-in to extract the token and preferences.
 * @returns {{ _user: { token: string } | undefined, copiedPrefs: Record<string, any> | undefined
 * }} Object containing the user token and copied preferences.
 */
export const parseMagicLink = () => {
  const { path } = parseLocation();
  const { encodedData } = path.match(MAGIC_LINK_REGEX)?.groups ?? {};

  if (!encodedData) {
    return { _user: undefined, copiedPrefs: undefined };
  }

  // Remove token from the URL
  goto('', { replaceState: true });

  /** @type {{ token: string } | undefined} */
  let _user = undefined;
  /** @type {Record<string, any> | undefined} */
  let copiedPrefs = undefined;

  try {
    const data = decodeMagicLinkData(encodedData);

    if (isObject(data) && typeof data.token === 'string') {
      _user = { token: data.token };

      if (isObject(data.prefs)) {
        copiedPrefs = data.prefs;
      }
    }
  } catch {
    //
  }

  return { _user, copiedPrefs };
};

/**
 * Find cached user info, including a compatible Netlify/Decap CMS user object.
 * @returns {Promise<Record<string, any> | undefined>} Cached user info, or undefined if not found.
 */
export const getUserCache = async () => {
  /**
   * Check whether the given value is a cached user.
   * @param {any} value Stored value.
   * @returns {boolean} Result.
   */
  const isUserCache = (value) => isObject(value) && typeof value.backendName === 'string';

  // Read the keys one at a time, stopping at the first hit. The empty object left by signing out of
  // Sveltia CMS isn’t one, so a later sign-in with Netlify/Decap CMS is still picked up
  return [USER_STORAGE_KEY, ...LEGACY_USER_STORAGE_KEYS].reduce(async (previous, key) => {
    const found = await previous;

    if (found) {
      return found;
    }

    const value = await LocalStorage.get(key);

    return isUserCache(value) ? value : undefined;
  }, /** @type {Promise<Record<string, any> | undefined>} */ (Promise.resolve(undefined)));
};

/**
 * Get the backend instance based on the cached user info or site config.
 * @param {Record<string, any> | undefined} _user Cached user info.
 * @returns {BackendService | undefined} Backend instance to be used.
 */
export const getBackend = (_user) => {
  // Determine the backend name based on the user cache or site config. Use the local backend if the
  // user cache is found and the backend name is `local`, which is used by Sveltia CMS, or `proxy`,
  // which is used by Netlify/Decap CMS when running the local proxy server. Otherwise, simply use
  // the backend name from the site config. This is to ensure that the user is signed in with the
  // correct backend, especially when the user cache is from a different backend than the current
  // site config.
  const _backendName =
    _user?.backendName === 'local' || _user?.backendName === 'proxy'
      ? 'local'
      : /** @type {InternalCmsConfig} */ (cmsConfig.current).backend.name;

  return selectBackend(_backendName);
};

/**
 * Verify the token passed with a magic link, and ask the user to confirm the account it belongs
 * to. Since anyone can craft a link, it could otherwise sign the user in to someone else’s account
 * without their knowledge, overwriting their session, and copy the settings in the link, such as a
 * deploy hook URL, to their device.
 * @param {string} token Token passed with the link.
 * @returns {Promise<User | undefined>} User info if the token is valid and the user has confirmed
 * the account, or `undefined` otherwise.
 */
export const confirmMagicLink = async (token) => {
  const _backend = getBackend(undefined);

  if (!_backend) {
    return undefined;
  }

  /** @type {User | undefined} */
  let account = undefined;

  auth.signingIn = true;

  try {
    // The user store is left untouched, so an existing session stays cached until the user agrees
    // to replace it
    account = /** @type {User | undefined} */ (await _backend.signIn({ token, auto: true }));
  } catch {
    //
  }

  auth.signingIn = false;

  if (!account) {
    return undefined;
  }

  // Copy the narrowed value rather than cast it in the object literal: esrap, which Svelte prints
  // compiled code with, turns a cast property value named like its key, e.g.
  // `{ a: /** @type {T} */ (a) }`, into invalid shorthand
  const _account = account;

  const confirmed = await new Promise((resolve) => {
    auth.magicLinkConfirmation = { account: _account, resolve };
  });

  auth.magicLinkConfirmation = undefined;

  return confirmed ? account : undefined;
};

/**
 * Copy the user preferences passed with a magic link. The deploy hook URL and its `Authorization`
 * header are only copied together, so a link that has a URL but no header can’t leave the header
 * already on this device to be sent to the new URL. The integration credentials are then marked as
 * belonging to the account the link signs in to, which the link itself can’t override.
 * @param {Record<string, any>} copiedPrefs Preferences passed with the link.
 * @param {User} account Account the link signs in to.
 */
export const copyMagicLinkPrefs = (copiedPrefs, account) => {
  // Credentials copied with an earlier link mustn’t stay for those this one lacks
  clearMagicLinkCredentials();

  if ('deployHookURL' in copiedPrefs) {
    delete prefs.deployHookAuthHeader;
  }

  Object.assign(prefs, copiedPrefs, {
    magicLinkAccount: { backendName: account.backendName, id: account.id, login: account.login },
  });
};

/**
 * Load the files and unpublished entries from the backend the user has just signed in to, then
 * start keeping them up to date.
 * @param {BackendService} _backend Backend.
 */
const loadRepositoryData = async (_backend) => {
  // Signing out while the data is being loaded discards it, and stops the polling started below
  const isStale = createStaleCheck();
  // The pull requests don’t depend on the files, so they’re requested at the same time. That needs
  // the branch they target, though: without one configured, the default branch is only known once
  // the files are being fetched, and every pull request would be dropped as targeting another
  // branch. They’re then requested once the files are there
  const branchKnown = !_backend.repository || !!_backend.repository.branch;
  const pullRequests = branchKnown ? startLoadingPullRequests() : undefined;

  await _backend.fetchFiles();

  // The pull requests requested early belong to the previous session as well, and are dropped
  if (isStale()) {
    return;
  }

  await loadUnpublishedEntries(pullRequests);

  if (isStale()) {
    return;
  }

  // The deploy state is a nicety, so it’s resolved in the background rather than delaying the UI.
  // The files were loaded from the branch head, which is the commit the production site is built
  // from, so it isn’t fetched again
  initDeployments({ head: repositoryHead.current });
  // From here on, someone else’s commits are picked up as they land
  startRemoteChangePolling();
};

/**
 * Check if the user info is cached, set the backend, and automatically start loading files if the
 * backend is Git-based and user’s auth token is found.
 */
export const signInAutomatically = async () => {
  resetError();

  const { _user: magicLinkUser, copiedPrefs } = parseMagicLink();
  /** @type {Record<string, any> | undefined} */
  let _user = magicLinkUser ? await confirmMagicLink(magicLinkUser.token) : undefined;
  // The account has been verified already if the user has accepted a magic link
  const verified = !!_user;

  // Fall back to the cached user, which also covers a declined or invalid magic link
  _user ??= await getUserCache();

  // Initialize the backend, which is needed on the login page
  const _backend = getBackend(_user);

  // If no cached user info is found, simply return as we cannot sign in automatically
  if (!_user) {
    return;
  }

  if (!verified && _backend) {
    // Temporarily populate the `user` store with the cache, otherwise it’s not updated in
    // `refreshAccessToken`
    user.account = /** @type {User} */ (_user);

    const { token, refreshToken } = _user;

    auth.signingIn = true;

    try {
      _user = /** @type {User} */ (await _backend.signIn({ token, refreshToken, auto: true }));
    } catch {
      // The local backend may throw if the file handle permission is not given
      _user = undefined;
      user.account = undefined;
    }
  }

  auth.signingIn = false;
  auth.unauthenticated = !_user;

  if (!_user || !_backend) {
    return;
  }

  // Use the cached user to start fetching files
  user.account = /** @type {User} */ (_user);

  // Copy user preferences passed with QR code, or drop the credentials copied with a link to
  // another account
  if (verified && copiedPrefs) {
    copyMagicLinkPrefs(copiedPrefs, user.account);
  } else {
    clearMagicLinkCredentials(user.account);
  }

  try {
    await loadRepositoryData(_backend);
  } catch (/** @type {any} */ ex) {
    // The API request may fail if the cached token has been expired or revoked. Then let the user
    // sign in again. 404 Not Found is also considered an authentication error.
    // https://docs.github.com/en/rest/overview/troubleshooting-the-rest-api#404-not-found-for-an-existing-resource
    if ([401, 403, 404].includes(ex.cause?.status)) {
      auth.unauthenticated = true;
    } else {
      logError(ex, 'dataFetch');
    }

    await clearUserCacheIfNeeded(ex);
  }
};

/**
 * Sign in with the given backend.
 * @param {string} _backendName Backend name to be used.
 * @param {string} [token] Personal Access Token (PAT) to be used for authentication.
 */
export const signInManually = async (_backendName, token) => {
  resetError();

  const _backend = selectBackend(_backendName);

  if (!_backend) {
    return;
  }

  let _user;

  // An account may be left in the store from a previous session whose data failed to load. It may
  // be someone else’s, so its tokens mustn’t be picked up while signing in, e.g. as renewed ones
  user.account = undefined;
  auth.signingIn = true;

  try {
    _user = await _backend.signIn({ token, auto: false });
  } catch (/** @type {any} */ ex) {
    auth.signingIn = false;
    auth.unauthenticated = true;

    if (!!token && ex.cause?.status === 401) {
      // If the user is signing in using a personal access token (PAT) and the token is invalid,
      // display a specific error message.
      logError(
        new Error('Invalid token', { cause: { message: _('sign_in_error.invalid_token') } }),
      );
    } else {
      logError(ex);
    }

    return;
  }

  auth.signingIn = false;
  auth.unauthenticated = !_user;

  if (!_user) {
    return;
  }

  clearMagicLinkCredentials(_user);
  user.account = _user;

  try {
    await loadRepositoryData(_backend);
  } catch (/** @type {any} */ ex) {
    logError(ex, 'dataFetch');
    await clearUserCacheIfNeeded(ex);
  }
};

/**
 * Sign out from the current backend.
 */
export const signOut = async () => {
  stopRemoteChangePolling();
  // A check for remote changes, or a load, still on its way belongs to this session, so it mustn’t
  // put its results back into the stores once they have been reset below
  dataGeneration.current += 1;

  // A failure, e.g. an IndexedDB error, mustn’t keep the user signed in
  try {
    await backend.current?.signOut();
  } catch (/** @type {any} */ ex) {
    // eslint-disable-next-line no-console
    console.error(ex);
  }

  await clearUserCache();
  clearIntegrationCredentials();

  selectBackend(undefined);
  dataLoaded.current = false;
  // The items of the files storing all the entries of a collection belong to this repository
  arrayFileItems.clear();
  repositoryHead.current = '';
  lockedBranch.current = undefined;
  mergeLockedBranch.current = undefined;
  unpublishedEntries.current = [];
  unpublishedEntriesLoaded.current = false;
  publishingBranches.current = [];
  // The fork belongs to the signed-out user, so the next user needs Open Authoring set up again
  forkPermissionRequest.current?.respond(false);
  forkedRepository.current = undefined;
  openAuthoringInitialized.current = false;
  resetDeployingEntries();
  resetDeployments();
  resetPageLiveness();

  const redirectURL = cmsConfig.current?.logout_redirect_url;

  if (redirectURL) {
    window.location.href = redirectURL;
  }
};
